/**
 * Triggers a browser download. Modern replacement for the unmaintained
 * `downloadjs` package, supporting the modes we actually use: a Blob or string
 * payload with a filename/mime, a data: URL, or a single same-origin/CORS URL
 * argument (fetched then saved). The package's legacy IE `msSaveBlob`,
 * old-Safari, and iframe fallbacks are intentionally dropped — `a[download]` is
 * universal across our supported browsers.
 */

type DownloadData = Blob | string | ArrayBuffer | ArrayBufferView;

const DEFAULT_MIME = 'application/octet-stream';

function saveHref(href: string, fileName: string, isObjectUrl: boolean): void {
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  if (isObjectUrl) {
    setTimeout(() => URL.revokeObjectURL(href), 250);
  }
}

export default function download(data: DownloadData, fileName?: string, mimeType?: string): void {
  // With no filename and no mime, a lone string is treated as a URL to fetch
  // and save (matches downloadjs's solo-URL mode).
  if (fileName == null && mimeType == null && typeof data === 'string' && data.length < 2048) {
    const inferredName = data.split('/').pop()?.split('?')[0] || 'download';
    const ajax = new XMLHttpRequest();
    ajax.open('GET', data, true);
    ajax.responseType = 'blob';
    ajax.onload = (e) =>
      download((e.target as XMLHttpRequest).response, inferredName, DEFAULT_MIME);
    ajax.send();
    return;
  }

  const name = fileName || 'download';
  const type = mimeType || DEFAULT_MIME;

  if (typeof data === 'string' && /^data:/.test(data)) {
    saveHref(data, name, false);
    return;
  }

  const blob = data instanceof Blob ? data : new Blob([data as BlobPart], {type});
  const objectUrl = URL.createObjectURL(blob);
  saveHref(objectUrl, name, true);
}
